import React, { Fragment, useMemo, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import FuzzySearch from "fuzzy-search";

import { GenericTooltip, HTMLTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { LightSwitch } from "@components/utilities";
import { SecondaryButton } from "@components/v2/buttons";
import {
  flip,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { prettyLogEvent } from "@internal/analytics";
import {
  CreativeMetricType,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { Select } from "@shared/selects";

import { useAvailableMetricsByIdAtom, useCollapseTopBar } from "../atoms";
import {
  generateHeroMetricId,
  useReportBodyState,
} from "../hooks/ca-control-state";

export function CreativeAnalyticsTopBar() {
  const [isExpanded] = useCollapseTopBar();

  return (
    <AnimatePresence initial={true}>
      {isExpanded && (
        <>
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ ease: "easeOut", duration: 0.2 }}
            className="h-20 flex flex-row items-center"
          >
            <CreativeAnalyticsMetrics />
            <GroupByNameSwitch />
          </motion.div>
          <div className="border-t-[1.5px] w-full border-v2-secondary" />
        </>
      )}
    </AnimatePresence>
  );
}

function GroupByNameSwitch() {
  const { groupAdsByName, setGroupAdsByName } = useReportBodyState();
  const { user } = useUser();

  return (
    <div className="ml-auto flex flex-row items-center">
      <GenericTooltip
        content={
          <div>
            Sums metrics for ads with
            <div>the same name across campaigns</div>
          </div>
        }
      >
        Group ads by name
      </GenericTooltip>
      <div className="ml-2 mr-5">
        <LightSwitch
          id="groupby-switch"
          size="medium"
          isSet={groupAdsByName}
          disabled={false}
          onChange={() => {
            const newGroupAdByName = !groupAdsByName;
            setGroupAdsByName(newGroupAdByName);
            prettyLogEvent.onClick({
              user,
              page: Pages.CREATIVE_ANALYTICS,
              additionalActionInfo: "group-ads-by-name-switch",
              data: { newGroupAdByName },
            });
          }}
        />
      </div>
    </div>
  );
}

function CreativeAnalyticsMetrics() {
  const { metrics: selectedMetrics, setMetrics: setSelectedMetrics } =
    useReportBodyState();
  const [availableMetricsById] = useAvailableMetricsByIdAtom();

  const metricIds = [...selectedMetrics];
  const visible = metricIds
    .slice(0, metricIds.length > 4 ? 3 : 4)
    .map((e) => availableMetricsById[e]);
  const rest = metricIds
    .slice(visible.length)
    .map((e) => availableMetricsById[e]);

  return (
    <div className="flex flex-row ml-5 items-center">
      <CreativeAnalyticsMetricManager />
      <HeroMetric />
      <div
        className={clsx(
          "flex flex-row flex-wrap self-end gap-x-3 gap-y-1 px-3",
          "w-[45rem] 3xl:w-[55rem] 4k:w-[80rem]",
        )}
      >
        {visible.map((metric) => {
          return (
            <Metric
              key={metric.id}
              metric={metric}
              onRemove={() => {
                const newSet = new Set(selectedMetrics);
                newSet.delete(metric.id);
                setSelectedMetrics(newSet);
              }}
            />
          );
        })}
        {rest.length > 0 && (
          <MoreMetricsButton
            metrics={rest}
            onRemove={(metric) => {
              const newSet = new Set(selectedMetrics);
              newSet.delete(metric.id);
              setSelectedMetrics(newSet);
            }}
          />
        )}
      </div>
    </div>
  );
}

function HeroMetric() {
  const {
    heroMetric,
    setHeroMetric,
    metrics,
    setMetrics,
    attributionModel,
    attributionWindow,
  } = useReportBodyState();
  return (
    <div className="w-[7rem] flex flex-row mr-2">
      <div className="border-l-[1.5px] mt-5 mr-5 ml-3 border-v2-secondary" />
      <Select
        label="KEY METRIC"
        value={heroMetric}
        options={["ROAS", "CAC"].map((metric) => ({
          value: metric,
          label: metric,
        }))}
        className="w-[5.5rem]"
        onChange={(newHeroMetric) => {
          setHeroMetric(newHeroMetric);
          const newHeroMetricId = generateHeroMetricId(
            newHeroMetric,
            attributionModel,
            attributionWindow,
          );
          if (metrics.has(newHeroMetricId)) {
            const newMetrics = new Set([...metrics]);
            newMetrics.delete(newHeroMetricId);
            setMetrics(newMetrics);
          }
        }}
      />
    </div>
  );
}

function CreativeAnalyticsMetricManager() {
  return (
    <Popover className="relative mt-5">
      {() => (
        <>
          <PopoverButton>
            <SecondaryButton>
              <i className="fa-regular fa-sharp fa-circle-plus" />
              <div>ADD METRIC</div>
            </SecondaryButton>
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              className={clsx(
                "absolute w-[20rem] h-[30rem] z-10 mt-2.5",
                "bg-v2-primary rounded-lg elevation-1 flex flex-col px-8 py-3",
              )}
            >
              <div>
                Select up to <span className="font-semibold">8 metrics</span>
              </div>
              <CreativeAnalyticsMetricsOptionsSelector />
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

function CreativeAnalyticsMetricsOptionsSelector() {
  const [searchText, setSearchText] = useState<string>();
  const { metrics: selectedMetrics, setMetrics: setSelectedMetrics } =
    useReportBodyState();
  const [availableMetricsById] = useAvailableMetricsByIdAtom();

  const options = useMemo(() => {
    return Object.values(availableMetricsById).filter(
      (i) =>
        (!i.pagesEnabled || i.pagesEnabled.creativeAnalytics) &&
        i.spec?.group !== CreativeMetricType.tradedesk &&
        i.spec?.group !== CreativeMetricType.twitter,
    );
  }, [availableMetricsById]);

  const filteredOptions = useMemo(() => {
    const searcher = new FuzzySearch(options, ["name"], { sort: true });
    return searcher.search(searchText).map((i) => i.id);
  }, [options, searchText]);

  return (
    <div className="relative">
      <Input
        className="mt-5 w-[15rem]"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Search metrics..."
        id="search-metrics"
        iconClassname="fa-magnifying-glass"
      />
      <div className="relative overflow-y-auto h-[22rem] mt-2">
        {filteredOptions.map((option) => {
          return (
            <div key={option}>
              <label className="flex flex-row items-center mt-4 ml-3 accent-cosmic mr-3">
                <input
                  type="checkbox"
                  checked={selectedMetrics.has(option)}
                  disabled={
                    !selectedMetrics.has(option) && selectedMetrics.size === 8
                  }
                  onChange={() => {
                    const newSet = new Set(selectedMetrics);
                    if (selectedMetrics.has(option)) {
                      newSet.delete(option);
                    } else {
                      newSet.add(option);
                    }
                    setSelectedMetrics(newSet);
                  }}
                  className="mr-3"
                />
                {availableMetricsById[option].name}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Input({
  id,
  onChange,
  value,
  className,
  placeholder,
  iconClassname,
}: {
  iconClassname?: string;
  id: string;
  className?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: string | undefined;
  placeholder: string;
}) {
  return (
    <label htmlFor={id} className={clsx("relative ", className)}>
      {!value && iconClassname && (
        <i
          className={clsx(
            "fa-regular fa-sharp pointer-events-none  absolute transform translate-y-1/2",
            "right-3 top-1 text-gray-1",
            iconClassname,
          )}
        />
      )}
      <input
        className={clsx(
          "text-sm font-normal rounded-md w-full py-2 px-3 border focus:outline-none",
          "focus:border-cosmic focus:ring-1 focus:ring-cosmic",
        )}
        autoFocus
        id={id}
        style={{ borderColor: "hsl(0deg 0% 80%)" }}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={onChange}
      />
    </label>
  );
}

function Metric({
  metric,
  onRemove,
}: {
  metric: SalesPageMetric;
  onRemove: () => void;
}) {
  return (
    <div
      className={clsx(
        "text-sm text-v2-primary rounded-3xl h-8 pl-4 pr-1.5",
        "flex items-center border-v2-action border-[1px] text-ellipsis overflow-hidden",
        "bg-v2-primary hover:bg-v2-secondary",
      )}
    >
      <HTMLTooltip
        html={metric.descriptionHTML}
        noInfoCircle
        className="truncate max-w-[160px]"
      >
        {metric.name}
      </HTMLTooltip>
      <i
        className={clsx(
          "flex items-center justify-center ml-1 fa-regular fa-sharp fa-xmark",
          "cursor-pointer text-v2-primary text-base rounded-full bg-v2-secondary",
          "px-[0.375rem] leading-[1.375rem]",
        )}
        onClick={onRemove}
      />
    </div>
  );
}

const MoreMetricsButton = ({
  metrics,
  onRemove,
}: {
  metrics: SalesPageMetric[];
  onRemove: (metric: SalesPageMetric) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "bottom-start",
    middleware: [flip()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <Popover>
      <PopoverButton
        as="button"
        className={clsx(
          "text-sm rounded-3xl h-8 px-4 flex items-center gap-2 border border-primary",
          {
            "bg-cosmic-5 hover:bg-cosmic-3": !isOpen,
            "bg-cosmic text-white": isOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        +{metrics.length} more
        <i
          className={clsx("fa-regular text-sm", {
            "fa-chevron-down": !isOpen,
            "fa-chevron-up": isOpen,
          })}
        ></i>
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
        show={isOpen}
      >
        <PopoverPanel
          ref={refs.setFloating}
          style={floatingStyles}
          className={clsx(
            "mt-2 absolute z-10 bg-v2-primary rounded-lg elevation-1 text-sm pt-2 pb-2 w-[250px]",
          )}
          {...getFloatingProps()}
        >
          {metrics.map((metric) => {
            return (
              <div
                key={metric.id}
                className={clsx(
                  "w-full text-left px-4 hover:bg-cosmic-4 h-8 flex items-center",
                )}
              >
                <span className="flex-1 truncate">{metric.name}</span>
                <button
                  className="w-6 h-6 rounded-full ml-0.5 hover:bg-v2-primary"
                  onClick={() => onRemove(metric)}
                >
                  <i className="fa-regular fa-sharp fa-xmark text-sm" />
                </button>
              </div>
            );
          })}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};
